<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Pencil } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDate } from '@yuksalish/ui'
import { getProduct, getProductUoms } from '../productService'
import {
  formatIdentifier,
  isManufacturedProduct,
  resolveProductImage,
} from '../productPresentation'
import type { EngineeringProduct, ProductUom } from '../types'

const route = useRoute()
const { t } = useI18n()
const client = useApiClient()
const auth = useAuthStore()
const item = ref<EngineeringProduct | null>(null)
const productUoms = ref<ProductUom[]>([])
const loading = ref(false)
const errorMessage = ref('')
const itemId = computed(() => Number(route.params.id))
const kind = computed(() =>
  route.meta.catalogKind === 'spare-parts' ? 'spare-parts' : 'raw-materials',
)
const expectedType = computed(() =>
  kind.value === 'spare-parts' ? 'spare_part' : 'raw_material',
)
const sectionTitle = computed(() =>
  kind.value === 'spare-parts'
    ? t('engineering.catalog.spareSingular')
    : t('engineering.catalog.rawSingular'),
)
const listPath = computed(() => `/${kind.value}`)
const imageUrl = computed(() => resolveProductImage(item.value?.photo_url ?? null))
const canManageProduct = computed(
  () => auth.user?.permissions.includes('products.update') ?? false,
)
const editPath = computed(() => `/${kind.value}/${itemId.value}/edit`)

async function loadItem(): Promise<void> {
  if (!Number.isInteger(itemId.value) || itemId.value <= 0) {
    errorMessage.value = t('engineering.catalog.invalidId')
    item.value = null
    productUoms.value = []
    return
  }
  loading.value = true
  errorMessage.value = ''
  try {
    const [result, uoms] = await Promise.all([
      getProduct(client, itemId.value),
      getProductUoms(client, itemId.value),
    ])
    if (result.type !== expectedType.value || isManufacturedProduct(result)) {
      item.value = null
      productUoms.value = []
      errorMessage.value = t('engineering.catalog.wrongSection', { section: sectionTitle.value })
      return
    }
    item.value = result
    productUoms.value = uoms
  } catch (error: unknown) {
    item.value = null
    productUoms.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

onMounted(loadItem)
watch([itemId, kind], loadItem)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="t('engineering.catalog.backTo', { section: sectionTitle })" :to="listPath">{{ t('engineering.catalog.backTo', { section: sectionTitle }) }}</RouterLink>
  </div>

  <div v-if="loading" class="page-state">{{ t('engineering.catalog.loadingItem') }}</div>
  <div v-else-if="errorMessage" class="page-state page-state--error">
    <p role="alert">{{ errorMessage }}</p>
    <button type="button" @click="loadItem">{{ t('engineering.action.retry') }}</button>
  </div>

  <template v-else-if="item">
    <div class="page-heading">
      <div>
        <p class="eyebrow">{{ sectionTitle }} · {{ item.sku }}</p>
        <h2>{{ item.name }}</h2>
        <p>{{ item.group?.name || item.type.replaceAll('_', ' ') }}</p>
      </div>
      <RouterLink v-if="canManageProduct" class="edit-link" :to="editPath">
        <Pencil :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.editAction.${kind}`) }}
      </RouterLink>
      <span
        v-else
        class="edit-link edit-link--disabled"
        :title="$t('engineering.productForm.permissionHint')"
        :aria-label="$t('engineering.productForm.permissionHint')"
      >
        <Pencil :size="16" aria-hidden="true" />
        {{ $t(`engineering.productForm.editAction.${kind}`) }}
      </span>
    </div>

    <section class="item-layout">
      <div class="item-media">
        <img v-if="imageUrl" :src="imageUrl" :alt="item.name">
        <div v-else aria-hidden="true">{{ item.name.slice(0, 1).toUpperCase() }}</div>
      </div>
      <div>
        <h3>{{ t('engineering.catalog.definition') }}</h3>
        <dl>
          <div><dt>{{ t('engineering.catalog.internalSku') }}</dt><dd>{{ formatIdentifier(item.internal_sku_code, t('engineering.catalog.notAssigned')) }}</dd></div>
          <div><dt>{{ t('engineering.catalog.barcode') }}</dt><dd>{{ formatIdentifier(item.ean13, t('engineering.catalog.notAssigned')) }}</dd></div>
          <div><dt>{{ t('engineering.catalog.unit') }}</dt><dd>{{ item.unit }}</dd></div>
          <div><dt>{{ t('engineering.product.uoms') }}</dt><dd>{{ productUoms.length || t('engineering.catalog.notSet') }}</dd></div>
          <div><dt>{{ t('engineering.catalog.minimumOrder') }}</dt><dd>{{ item.min_order_quantity ?? t('engineering.catalog.notSet') }}</dd></div>
          <div><dt>{{ t('engineering.catalog.group') }}</dt><dd>{{ item.group?.name || t('engineering.catalog.notAssigned') }}</dd></div>
          <div><dt>{{ t('engineering.catalog.created') }}</dt><dd>{{ formatDisplayDate(item.created_at) || '-' }}</dd></div>
        </dl>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.product.uoms') }}</h3>
          <p>{{ $t('engineering.product.uomsHelp') }}</p>
        </div>
      </div>
      <div v-if="productUoms.length" class="uom-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('engineering.product.uomCode') }}</th>
              <th>{{ $t('engineering.product.uomLabel') }}</th>
              <th>{{ $t('engineering.product.uomFactor') }}</th>
              <th>{{ $t('engineering.product.uomEntry') }}</th>
              <th>{{ $t('engineering.product.uomStatus') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="uom in productUoms" :key="uom.uom_code">
              <td><strong>{{ uom.uom_code }}</strong></td>
              <td>{{ uom.label }}</td>
              <td>{{ uom.to_base_factor }}</td>
              <td>{{ uom.is_fractional_allowed ? $t('engineering.product.fractionalAllowed') : $t('engineering.product.wholeOnly') }}</td>
              <td>
                <span v-if="uom.is_base">{{ $t('engineering.product.baseUom') }}</span>
                <span v-else>{{ uom.is_active === false || uom.is_active === 0 ? $t('engineering.product.inactive') : $t('engineering.product.activeAlt') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="page-state page-state--section">
        {{ $t('engineering.product.uomsEmpty') }}
      </div>
    </section>

    <section>
      <div class="section-heading">
        <h3>{{ t('engineering.catalog.tagsAndPrices') }}</h3>
        <span>{{ t('engineering.catalog.prices', { count: item.prices.length }) }}</span>
      </div>
      <div class="tags">
        <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
        <p v-if="!item.tags.length">{{ t('engineering.catalog.noTags') }}</p>
      </div>
    </section>
  </template>
</template>

<style scoped>
.back-link { display: inline-block; margin-bottom: 20px; color: #285b9a; font-size: 13px; font-weight: 700; }
.page-heading { display:flex; gap:16px; align-items:flex-start; justify-content:space-between; margin-bottom: 24px; }
.eyebrow { margin: 0 0 5px; color: #66726e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
h2 { margin: 0; font-size: 26px; letter-spacing: 0; }
.page-heading p:last-child { margin: 7px 0 0; color: #66726e; }
.edit-link { display:inline-flex; align-items:center; gap:8px; padding:9px 12px; border:1px solid #3568a8; color:#285b9a; font-size:13px; font-weight:700; text-decoration:none; white-space:nowrap; }
.edit-link--disabled { border-color:#b9c5c1; color:#6f7d78; background:#eef2f1; cursor:not-allowed; }
section { margin-bottom: 16px; padding: 22px; border: 1px solid #d7dfdc; background: #ffffff; }
.item-layout { display: grid; grid-template-columns: 180px minmax(0, 1fr); gap: 22px; }
.item-media { width: 100%; aspect-ratio: 1; overflow: hidden; border: 1px solid #d7dfdc; background: #f1f4f3; }
.item-media img { width: 100%; height: 100%; object-fit: contain; }
.item-media div { display: grid; width: 100%; height: 100%; color: #fff; background: #526b63; font-size: 52px; font-weight: 700; place-items: center; }
h3 { margin: 0 0 18px; font-size: 16px; letter-spacing: 0; }
dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin: 0; }
dt { color: #66726e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
dd { margin: 5px 0 0; overflow-wrap: anywhere; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; }
.section-heading p { margin: 0; color: #66726e; font-size: 13px; }
.section-heading span { color: #66726e; font-size: 12px; }
.uom-table { overflow-x: auto; }
.uom-table table { width: 100%; border-collapse: collapse; font-size: 13px; }
.uom-table th, .uom-table td { padding: 10px; border: 1px solid #e1e6e4; text-align: left; }
.uom-table th { color: #586660; background: #f3f6f5; font-size: 11px; text-transform: uppercase; }
.tags { display: flex; flex-wrap: wrap; gap: 7px; }
.tags span { padding: 4px 7px; border: 1px solid #bcc9c4; border-radius: 4px; font-size: 12px; }
.tags p { margin: 0; color: #66726e; }
.page-state { display: grid; min-height: 240px; color: #697671; place-items: center; }
.page-state--error { align-content: center; gap: 12px; color: #852d22; }
.page-state--section { min-height: auto; padding: 16px; background: #f3f6f5; }
@media (max-width: 620px) {
  .page-heading { align-items:stretch; flex-direction:column; }
  .item-layout { grid-template-columns: 1fr; }
  .item-media { max-width: 220px; }
  dl { grid-template-columns: 1fr; }
}
</style>
